/**
 * PrintFallbackService
 *
 * Purpose:
 *   Owns the print outage switch — "the Zebra is down" / "the Print API is
 *   down" — that routes printing around dead hardware. Where the print then
 *   goes is not this service's business; that is
 *   {@link resolvePrintRoute} in print-path.rules.ts. This service answers only
 *   "which mode is this device in", and persists the answer.
 *
 * Why the browser, per device:
 *   An outage is noticed by the person standing in front of the printer. Storing
 *   the mode in localStorage lets them act on it in the moment — no config
 *   change, no deploy, no waiting on anyone. The known cost is that it is per
 *   person per device: the operator at the next station still sends jobs to the
 *   dead Zebra. That is the accepted trade, and the banner plus the expiry below
 *   are what bound it.
 *
 * Notes:
 *   - Modelled on {@link PwaService}: private named keys behind typed accessors,
 *     no storage keys leaking to callers.
 *   - AppComponent calls `localStorage.clear()` on both login and logout, which
 *     on a shared warehouse PC can be daily. {@link preserveThrough} is how this
 *     key survives that — see its doc.
 *   - The mode expires on its own (see {@link _expiryHours}). A switch left on
 *     for a week is worse than no switch: it silently becomes the default, and
 *     nobody is watching a setting they forgot they set.
 */

import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { PrintFallbackMode } from '../domain/receiving-log/rules/print-path.rules';

@Injectable({
  providedIn: 'root',
})
export class PrintFallbackService {
  // #region Fields
  /** localStorage key: the selected mode. */
  private readonly _modeKey = 'printFallbackMode';
  /** localStorage key: when it was selected (epoch ms), for the expiry below. */
  private readonly _setAtKey = 'printFallbackModeSetAt';

  /**
   * How long a mode survives before reverting to `normal`.
   *
   * Twelve hours — comfortably longer than any single shift, so it never expires
   * out from under the person who set it, and short enough that a mode set on
   * Friday afternoon is not still routing Monday's labels through a browser
   * dialog. Checked on read, not on a timer: the app can be closed for most of
   * the window.
   */
  private readonly _expiryHours = 12;

  private readonly _mode$ = new BehaviorSubject<PrintFallbackMode>(this._readStoredMode());
  // #endregion

  // #region Reading
  /** The current mode, re-emitted whenever it changes. */
  public get mode$(): Observable<PrintFallbackMode> {
    return this._mode$.asObservable();
  }

  /** The current mode, for the synchronous callers (the print-path rules). */
  public get mode(): PrintFallbackMode {
    return this._mode$.getValue();
  }

  /** Whether printing is currently being routed around an outage. */
  public get isActive(): boolean {
    return this.mode !== 'normal';
  }

  /**
   * Short description of the active mode, for the banner and the settings row.
   * Says what is happening to the printing, not merely which switch is on —
   * "Zebra is down" alone tells an operator nothing about where their labels went.
   */
  public get activeDescription(): string {
    switch (this.mode) {
      case 'zebraDown':
        return 'Zebra label printer unavailable — labels print from a desktop computer only, copier sheets still print normally.';
      case 'copierDown':
        return 'Copier unavailable — copier sheets print from a desktop computer only, labels still print normally.';
      case 'printApiDown':
        return 'Printers unavailable — labels and copier sheets both print from a desktop computer only. Receiving logs still save.';
      default:
        return '';
    }
  }
  // #endregion

  // #region Writing
  /**
   * Sets the mode and stamps the time, so the expiry has something to measure
   * against. Setting `normal` clears both keys rather than storing "normal" —
   * absent and normal mean the same thing, and one representation of a state is
   * easier to reason about than two.
   *
   * SETTING THE MODE IT IS ALREADY IN EMITS NOTHING. A BehaviorSubject will
   * happily re-publish an identical value, and every subscriber treats an
   * emission as news: the Review step tears down and re-resolves its whole print
   * config on each one, and the Printing screen re-renders, which is enough to
   * re-fire the radio group that called this — a loop that froze the app. An
   * emission has to mean the mode CHANGED, or callers cannot use it to mean
   * anything.
   *
   * The timestamp is deliberately not re-stamped for an unchanged mode either.
   * The 12 hours run from when the operator reported the printer down, not from
   * the last time something happened to re-select it.
   */
  public setMode(mode: PrintFallbackMode): void {
    if (mode === 'normal') {
      this.clear();
      return;
    }
    if (mode === this.mode) { return; }

    localStorage.setItem(this._modeKey, mode);
    localStorage.setItem(this._setAtKey, String(Date.now()));
    this._mode$.next(mode);
  }

  /**
   * Back to normal printing — the banner's "Turn off" and the expiry both land
   * here. Silent when already normal, for the reason in {@link setMode}.
   *
   * The keys are removed before that check rather than after: `normal` is stored
   * as their absence, so clearing a stray key while already reading `normal`
   * tidies storage without being a state change worth telling anyone about.
   */
  public clear(): void {
    localStorage.removeItem(this._modeKey);
    localStorage.removeItem(this._setAtKey);
    if (this.mode === 'normal') { return; }
    this._mode$.next('normal');
  }

  /**
   * Runs an action that wipes localStorage (AppComponent's login/logout
   * `clear()`) with this service's keys carried across it.
   *
   * The alternative — exporting the key names so AppComponent can save and
   * restore them — puts this service's storage layout in a component that has no
   * other reason to know it. Signing in is not a statement about the printer: an
   * operator who flags the Zebra as down and then signs in for their shift
   * should not silently be routed back to it.
   *
   * @param action - The storage-clearing action to run.
   */
  public preserveThrough(action: () => void): void {
    const mode = localStorage.getItem(this._modeKey);
    const setAt = localStorage.getItem(this._setAtKey);

    action();

    if (mode) { localStorage.setItem(this._modeKey, mode); }
    if (setAt) { localStorage.setItem(this._setAtKey, setAt); }
  }
  // #endregion

  // #region Internals
  /**
   * The stored mode, or `normal` — treating anything unrecognized, unstamped or
   * expired as normal.
   *
   * The failure direction is deliberate: a bad read routes printing to the
   * backend, which is where it goes when everything works. Guessing "the printer
   * is down" from a corrupt value would take a working plant offline.
   */
  private _readStoredMode(): PrintFallbackMode {
    let stored: string | null = null;
    let setAt: string | null = null;
    try {
      stored = localStorage.getItem(this._modeKey);
      setAt = localStorage.getItem(this._setAtKey);
    } catch {
      // Storage can be unavailable (private mode, blocked cookies). Print normally.
      return 'normal';
    }

    if (stored !== 'zebraDown' && stored !== 'copierDown' && stored !== 'printApiDown') { return 'normal'; }

    const setAtMs = Number(setAt);
    if (!Number.isFinite(setAtMs) || setAtMs <= 0) { return 'normal'; }
    if (Date.now() - setAtMs > this._expiryHours * 60 * 60 * 1000) {
      // Expired: drop the keys now so this is decided once, not on every read.
      localStorage.removeItem(this._modeKey);
      localStorage.removeItem(this._setAtKey);
      return 'normal';
    }

    return stored;
  }
  // #endregion
}
